import { io } from 'socket.io-client';
import { ORDER_SOCKET_URL } from '../constants/endpoints';

const NAMESPACE = '/futures';

let socket = null;
let subscriberCount = 0;

function getSocket() {
  if (!socket) {
    socket = io(`${ORDER_SOCKET_URL}${NAMESPACE}`, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });
  }
  return socket;
}

function parsePayload(raw) {
  try {
    const body = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!body || body.code !== 200 || !body.data) {
      return null;
    }
    return body.data;
  } catch {
    return null;
  }
}

function barTimeKey(bar) {
  if (!bar) {
    return '';
  }
  if (bar.timestamp != null && !Number.isNaN(Number(bar.timestamp))) {
    return String(Number(bar.timestamp));
  }
  if (bar.openTime) {
    const parsed = Date.parse(bar.openTime);
    return Number.isNaN(parsed) ? String(bar.openTime) : String(parsed);
  }
  return '';
}

function dedupeBars(bars, maxBars = 100) {
  if (!Array.isArray(bars) || bars.length === 0) {
    return [];
  }
  const seen = new Set();
  const deduped = [];
  for (const bar of bars) {
    const key = barTimeKey(bar);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(bar);
  }
  deduped.sort((a, b) => {
    const ta = Number(barTimeKey(a)) || 0;
    const tb = Number(barTimeKey(b)) || 0;
    return ta - tb;
  });
  return deduped.slice(-maxBars);
}

function mergeBarList(prevBars, bar, maxBars = 100) {
  if (!bar) {
    return prevBars;
  }
  const next = Array.isArray(prevBars) ? [...prevBars] : [];
  const incomingKey = barTimeKey(bar);
  const index = next.findIndex((item) => barTimeKey(item) === incomingKey);
  if (index >= 0) {
    next[index] = { ...next[index], ...bar };
  } else {
    next.push(bar);
  }
  return dedupeBars(next, maxBars);
}

/**
 * 訂閱交割 K 線；回傳取消訂閱函式。
 */
export function subscribeFuturesChart(productId, { onSnapshot, onUpdate, maxBars = 100 } = {}) {
  // order 服務停用（ORDER_SOCKET_URL 為空）：不建立連線、不重連
  if (!ORDER_SOCKET_URL || !productId) {
    return () => {};
  }

  const sock = getSocket();
  subscriberCount += 1;
  const normalizedId = Number(productId);

  const onSnapshotEvent = (raw) => {
    const data = parsePayload(raw);
    if (
      data?.pushType === 'SNAPSHOT'
      && Number(data.productId) === normalizedId
      && Array.isArray(data.bars)
    ) {
      onSnapshot?.(data.bars);
    }
  };

  const onUpdateEvent = (raw) => {
    const data = parsePayload(raw);
    if (
      data?.pushType === 'UPDATE'
      && Number(data.productId) === normalizedId
      && data.bar
    ) {
      onUpdate?.(data.bar, mergeBarList);
    }
  };

  sock.on('chartSnapshot', onSnapshotEvent);
  sock.on('chartUpdate', onUpdateEvent);

  const emitSubscribe = () => sock.emit('subscribeChart', normalizedId);
  if (sock.connected) {
    emitSubscribe();
  } else {
    sock.once('connect', emitSubscribe);
  }

  return () => {
    sock.off('chartSnapshot', onSnapshotEvent);
    sock.off('chartUpdate', onUpdateEvent);
    if (sock.connected) {
      sock.emit('unsubscribeChart', normalizedId);
    }
    subscriberCount -= 1;
    if (subscriberCount <= 0 && socket) {
      socket.disconnect();
      socket = null;
      subscriberCount = 0;
    }
  };
}

export { mergeBarList, dedupeBars };

/**
 * 訂閱局即時賠率。
 */
export function subscribeFuturesRoundOdds(roundCode, { onUpdate } = {}) {
  if (!ORDER_SOCKET_URL || !roundCode) {
    return () => {};
  }

  const sock = getSocket();
  subscriberCount += 1;
  const code = String(roundCode).trim();

  const onOddsEvent = (raw) => {
    const data = parsePayload(raw);
    if (data?.roundCode === code) {
      onUpdate?.(data);
    }
  };

  sock.on('oddsUpdate', onOddsEvent);

  const emitSubscribe = () => sock.emit('subscribeRoundOdds', code);
  if (sock.connected) {
    emitSubscribe();
  } else {
    sock.once('connect', emitSubscribe);
  }

  return () => {
    sock.off('oddsUpdate', onOddsEvent);
    if (sock.connected) {
      sock.emit('unsubscribeRoundOdds', code);
    }
    subscriberCount -= 1;
    if (subscriberCount <= 0 && socket) {
      socket.disconnect();
      socket = null;
      subscriberCount = 0;
    }
  };
}
